import React, { useState, useEffect } from 'react';
import { Users, UserPlus, Zap, Trash2, ChevronDown, ChevronUp, Search, Filter, CheckCircle, Clock, AlertCircle, RefreshCw } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AdminJuryAssignmentSection = ({ token }) => {
  const [juryMembers, setJuryMembers] = useState([]);
  const [assignments, setAssignments] = useState(null);
  const [teamsForAssignment, setTeamsForAssignment] = useState([]);
  const [loading, setLoading] = useState(true);
  const [assigning, setAssigning] = useState(false);
  const [expandedJury, setExpandedJury] = useState(null);
  const [selectedTeams, setSelectedTeams] = useState([]);
  const [selectedJuryForAssign, setSelectedJuryForAssign] = useState(null);
  const [showAssignModal, setShowAssignModal] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterTheme, setFilterTheme] = useState('all');

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [juryRes, assignmentsRes, teamsRes] = await Promise.all([
        axios.get(`${API}/admin/jury`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/admin/jury/assignments`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/admin/submissions-for-assignment`, { headers: { Authorization: `Bearer ${token}` } })
      ]);
      
      setJuryMembers(juryRes.data.jury || []);
      setAssignments(assignmentsRes.data);
      setTeamsForAssignment(teamsRes.data.teams || []);
    } catch (err) {
      toast.error('Failed to fetch data');
    } finally {
      setLoading(false);
    }
  };

  const handleAutoAssign = async () => {
    if (!window.confirm('This will automatically assign unassigned teams to jury members based on their expertise. Continue?')) return;
    
    setAssigning(true);
    try {
      const response = await axios.post(`${API}/admin/jury/auto-assign`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(response.data.message);
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Auto-assign failed');
    } finally {
      setAssigning(false);
    }
  };

  const handleManualAssign = async () => {
    if (!selectedJuryForAssign || selectedTeams.length === 0) {
      toast.error('Please select a jury member and at least one team');
      return;
    }
    
    setAssigning(true);
    try {
      const response = await axios.post(
        `${API}/admin/jury/assign-teams?jury_id=${selectedJuryForAssign}`,
        selectedTeams,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(response.data.message);
      setSelectedTeams([]);
      setSelectedJuryForAssign(null);
      setShowAssignModal(false);
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Assignment failed');
    } finally {
      setAssigning(false);
    }
  };

  const handleRemoveAssignment = async (submissionId, juryId, juryName) => {
    if (!window.confirm(`Remove ${juryName} from this submission?`)) return;
    
    try {
      await axios.delete(
        `${API}/admin/jury/remove-assignment?submission_id=${submissionId}&jury_id=${juryId}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Assignment removed');
      fetchData();
    } catch (err) {
      toast.error('Failed to remove assignment');
    }
  };

  const toggleTeamSelection = (teamId) => {
    setSelectedTeams(prev => 
      prev.includes(teamId) 
        ? prev.filter(id => id !== teamId)
        : [...prev, teamId]
    );
  };

  const selectAllUnassigned = () => {
    const unassigned = teamsForAssignment
      .filter(t => t.has_submission && t.assigned_jury.length === 0)
      .map(t => t.team_id);
    setSelectedTeams(unassigned);
  };

  // Filter teams
  const filteredTeams = teamsForAssignment.filter(team => {
    const matchesSearch = team.team_name.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesTheme = filterTheme === 'all' || team.theme === filterTheme;
    return matchesSearch && matchesTheme;
  });

  const themes = [...new Set(teamsForAssignment.map(t => t.theme).filter(Boolean))];

  if (loading) {
    return <div className="flex justify-center py-8"><div className="animate-spin h-8 w-8 border-4 border-purple-500 border-t-transparent rounded-full"></div></div>;
  }

  return (
    <div className="space-y-6" data-testid="jury-assignment-section">
      {/* Header */}
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
        <div>
          <h2 className="text-xl font-bold text-gray-900">Jury Team Assignment</h2>
          <p className="text-sm text-gray-500">Assign paid teams to jury members for evaluation</p>
        </div>
        <div className="flex gap-3">
          <button
            onClick={fetchData}
            className="flex items-center gap-2 px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200"
          >
            <RefreshCw size={18} />
            Refresh
          </button>
          <button
            onClick={handleAutoAssign}
            disabled={assigning}
            className="flex items-center gap-2 px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 disabled:opacity-50"
            data-testid="auto-assign-btn"
          >
            <Zap size={18} />
            {assigning ? 'Assigning...' : 'Auto-Assign All'}
          </button>
          <button
            onClick={() => setShowAssignModal(true)}
            className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
            data-testid="manual-assign-btn"
          >
            <UserPlus size={18} />
            Manual Assign
          </button>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <div className="bg-purple-50 rounded-lg p-4">
          <div className="text-2xl font-bold text-purple-600">{assignments?.stats?.total_jury || 0}</div>
          <div className="text-sm text-gray-600">Total Jury</div>
        </div>
        <div className="bg-blue-50 rounded-lg p-4">
          <div className="text-2xl font-bold text-blue-600">{assignments?.stats?.total_submissions || 0}</div>
          <div className="text-sm text-gray-600">Total Submissions</div>
        </div>
        <div className="bg-orange-50 rounded-lg p-4">
          <div className="text-2xl font-bold text-orange-600">{assignments?.stats?.unassigned_count || 0}</div>
          <div className="text-sm text-gray-600">Unassigned</div>
        </div>
        <div className="bg-green-50 rounded-lg p-4">
          <div className="text-2xl font-bold text-green-600">
            {(assignments?.jury_assignments || []).reduce((acc, j) => acc + j.scored_count, 0)}
          </div>
          <div className="text-sm text-gray-600">Scores Submitted</div>
        </div>
      </div>

      {/* Jury Assignments Overview */}
      <div className="bg-white rounded-lg border">
        <div className="px-4 py-3 border-b bg-gray-50">
          <h3 className="font-semibold">Jury Assignments Overview</h3>
        </div>
        <div className="divide-y">
          {(assignments?.jury_assignments || []).map(jury => (
            <div key={jury.jury_id} className="overflow-hidden">
              <button
                onClick={() => setExpandedJury(expandedJury === jury.jury_id ? null : jury.jury_id)}
                className="w-full px-4 py-3 flex justify-between items-center hover:bg-gray-50"
              >
                <div className="flex items-center gap-4">
                  <div className={`w-3 h-3 rounded-full ${jury.is_active ? 'bg-green-500' : 'bg-gray-300'}`} />
                  <div className="text-left">
                    <span className="font-medium">{jury.jury_name}</span>
                    <div className="flex gap-2 mt-1">
                      {(jury.expertise || []).slice(0, 2).map(e => (
                        <span key={e} className="text-xs bg-blue-100 text-blue-700 px-2 py-0.5 rounded">{e}</span>
                      ))}
                    </div>
                  </div>
                </div>
                <div className="flex items-center gap-6">
                  <div className="text-center">
                    <div className="text-lg font-bold">{jury.assigned_teams.length}</div>
                    <div className="text-xs text-gray-500">Assigned</div>
                  </div>
                  <div className="text-center">
                    <div className="text-lg font-bold text-green-600">{jury.scored_count}</div>
                    <div className="text-xs text-gray-500">Scored</div>
                  </div>
                  <div className="text-center">
                    <div className="text-lg font-bold text-orange-600">{jury.pending_count}</div>
                    <div className="text-xs text-gray-500">Pending</div>
                  </div>
                  {expandedJury === jury.jury_id ? <ChevronUp size={20} /> : <ChevronDown size={20} />}
                </div>
              </button>
              
              {expandedJury === jury.jury_id && (
                <div className="px-4 pb-4 bg-gray-50">
                  {jury.assigned_teams.length === 0 ? (
                    <p className="text-gray-500 text-sm py-2">No teams assigned yet</p>
                  ) : (
                    <div className="space-y-2 mt-2">
                      {jury.assigned_teams.map(team => (
                        <div key={team.submission_id} className="flex justify-between items-center bg-white rounded-lg px-3 py-2 border">
                          <div>
                            <span className="font-medium">{team.team_name}</span>
                            <span className="ml-2 text-xs text-gray-500">{team.theme}</span>
                          </div>
                          <div className="flex items-center gap-3">
                            <span className={`px-2 py-0.5 rounded-full text-xs ${
                              team.scored ? 'bg-green-100 text-green-700' : 'bg-yellow-100 text-yellow-700'
                            }`}>
                              {team.scored ? 'Scored' : 'Pending'}
                            </span>
                            <button
                              onClick={() => handleRemoveAssignment(team.submission_id, jury.jury_id, jury.jury_name)}
                              className="p-1 text-red-500 hover:bg-red-50 rounded"
                              title="Remove Assignment"
                            >
                              <Trash2 size={16} />
                            </button>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </div>
          ))}
        </div>
      </div>

      {/* Unassigned Submissions Alert */}
      {(assignments?.unassigned_submissions || []).length > 0 && (
        <div className="bg-orange-50 border border-orange-200 rounded-lg p-4">
          <div className="flex items-center gap-2 mb-2">
            <AlertCircle className="text-orange-600" size={20} />
            <h4 className="font-medium text-orange-800">
              {assignments.unassigned_submissions.length} Unassigned Submissions
            </h4>
          </div>
          <p className="text-sm text-orange-700 mb-3">
            The following teams have submitted but not yet assigned to any jury:
          </p>
          <div className="flex flex-wrap gap-2">
            {assignments.unassigned_submissions.slice(0, 10).map(sub => (
              <span key={sub.submission_id} className="bg-white px-3 py-1 rounded-full text-sm border border-orange-200">
                {sub.team_name}
              </span>
            ))}
            {assignments.unassigned_submissions.length > 10 && (
              <span className="text-orange-600 text-sm">+{assignments.unassigned_submissions.length - 10} more</span>
            )}
          </div>
        </div>
      )}

      {/* Manual Assignment Modal */}
      {showAssignModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl max-w-4xl w-full max-h-[90vh] overflow-hidden">
            <div className="px-6 py-4 border-b flex justify-between items-center">
              <h3 className="text-lg font-bold">Manual Team Assignment</h3>
              <button onClick={() => setShowAssignModal(false)} className="text-gray-500 hover:text-gray-700">✕</button>
            </div>
            
            <div className="p-6 overflow-y-auto max-h-[calc(90vh-200px)]">
              {/* Select Jury */}
              <div className="mb-6">
                <label className="block text-sm font-medium mb-2">Select Jury Member *</label>
                <select
                  value={selectedJuryForAssign || ''}
                  onChange={(e) => setSelectedJuryForAssign(e.target.value)}
                  className="w-full border rounded-lg px-3 py-2"
                  data-testid="select-jury-dropdown"
                >
                  <option value="">Choose a jury member...</option>
                  {juryMembers.filter(j => j.is_active).map(jury => (
                    <option key={jury.id} value={jury.id}>
                      {jury.name} - {(jury.expertise || []).join(', ') || 'All Themes'}
                    </option>
                  ))}
                </select>
              </div>

              {/* Filters */}
              <div className="flex gap-4 mb-4">
                <div className="flex-1 relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={18} />
                  <input
                    type="text"
                    placeholder="Search teams..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="w-full border rounded-lg pl-10 pr-3 py-2"
                  />
                </div>
                <select
                  value={filterTheme}
                  onChange={(e) => setFilterTheme(e.target.value)}
                  className="border rounded-lg px-3 py-2"
                >
                  <option value="all">All Themes</option>
                  {themes.map(theme => (
                    <option key={theme} value={theme}>{theme}</option>
                  ))}
                </select>
                <button
                  onClick={selectAllUnassigned}
                  className="px-3 py-2 bg-gray-100 rounded-lg hover:bg-gray-200 text-sm"
                >
                  Select Unassigned
                </button>
              </div>

              {/* Teams List */}
              <div className="border rounded-lg overflow-hidden">
                <div className="max-h-[300px] overflow-y-auto">
                  <table className="w-full">
                    <thead className="bg-gray-50 sticky top-0">
                      <tr>
                        <th className="w-10 px-3 py-2"></th>
                        <th className="text-left px-3 py-2 text-sm font-medium text-gray-600">Team</th>
                        <th className="text-left px-3 py-2 text-sm font-medium text-gray-600">Theme</th>
                        <th className="text-center px-3 py-2 text-sm font-medium text-gray-600">Status</th>
                        <th className="text-center px-3 py-2 text-sm font-medium text-gray-600">Assigned</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y">
                      {filteredTeams.filter(t => t.has_submission).map(team => (
                        <tr 
                          key={team.team_id} 
                          className={`hover:bg-gray-50 cursor-pointer ${selectedTeams.includes(team.team_id) ? 'bg-purple-50' : ''}`}
                          onClick={() => toggleTeamSelection(team.team_id)}
                        >
                          <td className="px-3 py-2">
                            <input
                              type="checkbox"
                              checked={selectedTeams.includes(team.team_id)}
                              onChange={() => {}}
                              className="rounded"
                            />
                          </td>
                          <td className="px-3 py-2 font-medium">{team.team_name}</td>
                          <td className="px-3 py-2 text-sm text-gray-600">{team.theme}</td>
                          <td className="px-3 py-2 text-center">
                            <span className={`px-2 py-0.5 rounded-full text-xs ${
                              team.status === 'submitted' ? 'bg-blue-100 text-blue-700' :
                              team.status === 'no_submission' ? 'bg-gray-100 text-gray-600' :
                              'bg-green-100 text-green-700'
                            }`}>
                              {team.status}
                            </span>
                          </td>
                          <td className="px-3 py-2 text-center">
                            {team.assigned_jury.length > 0 ? (
                              <span className="text-green-600 text-sm">{team.assigned_jury.length} jury</span>
                            ) : (
                              <span className="text-orange-600 text-sm">Unassigned</span>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>

              <div className="mt-4 text-sm text-gray-600">
                Selected: {selectedTeams.length} team(s)
              </div>
            </div>

            <div className="px-6 py-4 border-t bg-gray-50 flex justify-end gap-3">
              <button
                onClick={() => {setShowAssignModal(false); setSelectedTeams([]); setSelectedJuryForAssign(null);}}
                className="px-4 py-2 text-gray-600 hover:bg-gray-200 rounded-lg"
              >
                Cancel
              </button>
              <button
                onClick={handleManualAssign}
                disabled={assigning || !selectedJuryForAssign || selectedTeams.length === 0}
                className="px-6 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 disabled:opacity-50"
                data-testid="confirm-assign-btn"
              >
                {assigning ? 'Assigning...' : `Assign ${selectedTeams.length} Team(s)`}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminJuryAssignmentSection;
